import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { toCamelCase } from '../../grid/utils';
import type {
  MomSalaryReportGridRequest,
  MomSalaryReportOptions,
  MomSalaryReportResult,
} from '../types';

type MomSalaryReportApiResponse<T> = {
  Code?: number;
  data?: T;
};

type MomSalaryReportErrorPayload = {
  Error?: string;
};

const MOM_SALARY_REPORT_API = '/umbraco/api/MomSalaryReport';

const getResponseCode = <T>(response: MomSalaryReportApiResponse<T>): number | undefined =>
  response.Code;

const getResponseData = <T>(response: MomSalaryReportApiResponse<T>): T | undefined =>
  response.data;

const getResponseError = <T>(response: MomSalaryReportApiResponse<T>): string => {
  const payload = getResponseData(response) as MomSalaryReportErrorPayload | undefined;
  return payload?.Error || 'Failed to load MoM salary report';
};

const transformResponse = <T>(response: MomSalaryReportApiResponse<unknown>): T => {
  if (getResponseCode(response) !== 1) {
    throw new Error(getResponseError(response));
  }

  return toCamelCase(getResponseData(response) ?? {}) as T;
};

export const transformMomSalaryOptionsResponse = (
  response: MomSalaryReportApiResponse<unknown>
): MomSalaryReportOptions => transformResponse<MomSalaryReportOptions>(response);

export const transformMomSalaryReportResponse = (
  response: MomSalaryReportApiResponse<unknown>
): MomSalaryReportResult => transformResponse<MomSalaryReportResult>(response);

export const momSalaryReportApi = createApi({
  reducerPath: 'momSalaryReportApi',
  baseQuery,
  tagTypes: ['MomSalaryReport'],
  endpoints: (builder) => ({
    getOptions: builder.query<MomSalaryReportOptions, void>({
      query: () => `${MOM_SALARY_REPORT_API}/GetOptions`,
      transformResponse: transformMomSalaryOptionsResponse,
      keepUnusedDataFor: 300,
    }),
    generateReport: builder.query<MomSalaryReportResult, MomSalaryReportGridRequest>({
      query: (body) => ({
        url: `${MOM_SALARY_REPORT_API}/GenerateGridReport`,
        method: 'POST',
        body,
      }),
      transformResponse: transformMomSalaryReportResponse,
    }),
  }),
});

export const { useGetOptionsQuery, useLazyGenerateReportQuery } = momSalaryReportApi;
